import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsApi, usersApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Search, TrendingUp, Upload, User,
  Phone, Mail, Calendar, Sparkles, ArrowRight,
  Building2, FileSpreadsheet, CheckCircle, Clock, X
} from 'lucide-react';
import { toast } from 'sonner';

const EAGLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/h25ygx96_Transparent%20BG%20Eagle.png';

const stages = [
  { value: 'prospect', label: 'Prospect', color: '#106EFD' },
  { value: 'pitching', label: 'Pitching', color: '#40CFDE' },
  { value: 'secured_lead', label: 'Secured Lead', color: '#FFD166' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: '#E171EA' },
  { value: 'closed', label: 'Closed', color: '#37C563' },
  { value: 'abandoned', label: 'Abandoned', color: '#6B7280' },
];

const companyTypes = ['Fintech', 'Attorney', 'Bank', 'Non-Bank Lender', 'Consultant', 'Credit Union', 'Other'];

const Leads = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // list, pipeline
  const [newLead, setNewLead] = useState({
    company_name: '', company_type: '', contact_name: '', contact_email: '',
    contact_phone: '', source: '', stage: 'prospect', estimated_value: 0,
    owner_id: '', priority: 'medium', priority_score: 50, notes: '', offer: '',
  });

  const fetchData = async () => {
    try {
      const [leadsRes, usersRes] = await Promise.all([
        leadsApi.getAll(stageFilter !== 'all' ? stageFilter : undefined),
        usersApi.getAll()
      ]);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);
    } catch { toast.error('Failed to fetch leads'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [stageFilter]);

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      await leadsApi.create(newLead);
      toast.success('Lead created!');
      setDialogOpen(false);
      setNewLead({ company_name: '', company_type: '', contact_name: '', contact_email: '',
        contact_phone: '', source: '', stage: 'prospect', estimated_value: 0,
        owner_id: '', priority: 'medium', priority_score: 50, notes: '', offer: '' });
      fetchData();
    } catch { toast.error('Failed to create lead'); }
  };

  const handleFileImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await leadsApi.import(file);
      toast.success(`Imported ${res.data.imported} leads (${res.data.skipped} skipped)`);
      setImportDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        const updateData = { ...lead, stage: newStage };
        delete updateData.id; delete updateData.created_by; delete updateData.created_at;
        delete updateData.updated_at; delete updateData.is_deprioritized;
        await leadsApi.update(leadId, updateData);
        setLeads(leads.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
        toast.success('Stage updated');
      }
    } catch { toast.error('Failed to update'); }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !search ||
      lead.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.contact_email?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || lead.company_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStageColor = (stage) => stages.find(s => s.value === stage)?.color || '#6B7280';
  const getStageLabel = (stage) => stages.find(s => s.value === stage)?.label || stage;
  const getPriorityStyle = (p) => p === 'high' ? 'bg-red-100 text-red-700' : p === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';
  const activeLeads = leads.filter(l => !['closed', 'abandoned'].includes(l.stage));

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Business Development</h1>
          <p className="text-navy/60 mt-1">Manage your pipeline and close deals</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-leads-btn">
                <Upload className="w-4 h-4 mr-2" /> Import Sheet
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle className="font-['Amaranth']">Import Leads from Spreadsheet</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="border-2 border-dashed border-navy/20 rounded-xl p-8 text-center hover:border-[#40CFDE] transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-[#40CFDE]', 'bg-[#40CFDE]/5'); }}
                  onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-[#40CFDE]', 'bg-[#40CFDE]/5'); }}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileImport(f); }}
                  data-testid="import-dropzone"
                >
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-[#40CFDE]" />
                  <p className="font-medium text-[#1A2B5E]">Drop your file here or click to browse</p>
                  <p className="text-sm text-navy/50 mt-1">Supports .xlsx and .csv files</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.csv"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileImport(f); }}
                  />
                </div>
                <div className="bg-[#F5F8FF] rounded-lg p-4 text-sm text-navy/60">
                  <p className="font-medium text-[#1A2B5E] mb-2">Expected columns:</p>
                  <p>Opportunity, Type, Name, Email, Stage, Notes, Last Contact, Send Article, Send Email, Text, Priority</p>
                </div>
                {importing && (
                  <div className="flex items-center gap-2 text-[#40CFDE]">
                    <div className="w-5 h-5 loading-spinner" />
                    <span>Importing leads...</span>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Amaranth']">Add New Lead</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLead} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2"><Label>Opportunity (Company) *</Label><Input value={newLead.company_name} onChange={e => setNewLead({...newLead, company_name: e.target.value})} placeholder="Company name" required data-testid="lead-company-input" /></div>
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={newLead.company_type} onValueChange={v => setNewLead({...newLead, company_type: v})}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{companyTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Stage</Label>
                    <Select value={newLead.stage} onValueChange={v => setNewLead({...newLead, stage: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Contact Name</Label><Input value={newLead.contact_name} onChange={e => setNewLead({...newLead, contact_name: e.target.value})} placeholder="Last, First" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={newLead.contact_email} onChange={e => setNewLead({...newLead, contact_email: e.target.value})} placeholder="email@company.com" /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={newLead.contact_phone} onChange={e => setNewLead({...newLead, contact_phone: e.target.value})} placeholder="+1 (555) 123-4567" /></div>
                  <div className="space-y-2"><Label>Priority</Label>
                    <Select value={newLead.priority} onValueChange={v => setNewLead({...newLead, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2"><Label>Offer</Label><Textarea value={newLead.offer} onChange={e => setNewLead({...newLead, offer: e.target.value})} placeholder="What would the offer be?" rows={2} /></div>
                  <div className="space-y-2 col-span-2"><Label>Notes</Label><Textarea value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})} placeholder="Internal notes..." rows={2} /></div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="create-lead-btn">Create Lead</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Total Leads</p>
          <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">{leads.length}</p>
        </CardContent></Card>
        <Card className="bg-white border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Active Pipeline</p>
          <p className="text-3xl font-bold text-[#106EFD] font-['Amaranth'] mt-1">{activeLeads.length}</p>
        </CardContent></Card>
        <Card className="bg-white border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Closed Won</p>
          <p className="text-3xl font-bold text-[#37C563] font-['Amaranth'] mt-1">{leads.filter(l => l.stage === 'closed').length}</p>
        </CardContent></Card>
        <Card className="bg-white border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Proposals Out</p>
          <p className="text-3xl font-bold text-[#E171EA] font-['Amaranth'] mt-1">{leads.filter(l => l.stage === 'proposal_sent').length}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
          <Input placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="lead-search-input" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[170px]" data-testid="stage-filter">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[170px]" data-testid="type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {companyTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border rounded-lg overflow-hidden">
          <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-[#1A2B5E] text-white' : 'text-navy/60 hover:bg-navy/5'}`} data-testid="view-list-btn">List</button>
          <button onClick={() => setViewMode('pipeline')} className={`px-3 py-1.5 text-sm ${viewMode === 'pipeline' ? 'bg-[#1A2B5E] text-white' : 'text-navy/60 hover:bg-navy/5'}`} data-testid="view-pipeline-btn">Pipeline</button>
        </div>
      </div>

      {/* Pipeline View */}
      {viewMode === 'pipeline' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 min-h-[400px]">
          {stages.map(stage => {
            const stageLeads = filteredLeads.filter(l => l.stage === stage.value);
            return (
              <div key={stage.value} className="space-y-2">
                <div className="flex items-center gap-2 px-2 py-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span className="text-sm font-medium text-[#1A2B5E]">{stage.label}</span>
                  <span className="text-xs text-navy/40 ml-auto">{stageLeads.length}</span>
                </div>
                <div className="space-y-2">
                  {stageLeads.map(lead => (
                    <Card
                      key={lead.id}
                      className="border-navy/10 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      data-testid={`pipeline-card-${lead.id}`}
                    >
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm text-[#1A2B5E] truncate">{lead.company_name}</h4>
                        {lead.company_type && <Badge className="text-[10px] mt-1 bg-navy/5 text-navy/60">{lead.company_type}</Badge>}
                        {lead.contact_name && <p className="text-xs text-navy/50 mt-1 truncate">{lead.contact_name}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <Badge className={`text-[10px] ${getPriorityStyle(lead.priority)}`}>{lead.priority}</Badge>
                          {lead.last_contact && <span className="text-[10px] text-navy/40">{new Date(lead.last_contact).toLocaleDateString()}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card className="bg-white border-navy/10">
          <CardContent className="p-0">
            {filteredLeads.length === 0 ? (
              <div className="py-16 text-center">
                <img src={EAGLE_MASCOT} alt="Eagle" className="w-24 h-24 mx-auto mb-3" />
                <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No leads found</h3>
                <p className="text-navy/60">Import a spreadsheet or add leads manually</p>
              </div>
            ) : (
              <div className="divide-y divide-navy/5">
                {filteredLeads.map(lead => (
                  <div
                    key={lead.id}
                    className="p-4 hover:bg-navy/5 transition-colors cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-[#1A2B5E]">{lead.company_name}</h4>
                          {lead.company_type && <Badge className="bg-navy/5 text-navy/60 text-xs">{lead.company_type}</Badge>}
                          <Badge className={`text-xs ${getPriorityStyle(lead.priority)}`}>{lead.priority}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-navy/60">
                          {lead.contact_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {lead.contact_name}</span>}
                          {lead.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.contact_email}</span>}
                          {lead.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.contact_phone}</span>}
                        </div>
                        {lead.notes && <p className="text-sm text-navy/50 mt-1 line-clamp-1">{lead.notes}</p>}
                        {lead.offer && <p className="text-xs text-[#E171EA] mt-1 line-clamp-1">Offer: {lead.offer}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getStageColor(lead.stage) }} />
                          <span className="text-sm font-medium" style={{ color: getStageColor(lead.stage) }}>{getStageLabel(lead.stage)}</span>
                        </div>
                        {lead.last_contact && (
                          <span className="text-xs text-navy/40 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(lead.last_contact).toLocaleDateString()}
                          </span>
                        )}
                        <div className="flex gap-1">
                          {lead.send_email && <Mail className="w-3.5 h-3.5 text-[#106EFD]" />}
                          {lead.send_article && <FileSpreadsheet className="w-3.5 h-3.5 text-[#E171EA]" />}
                          {lead.send_text && <Phone className="w-3.5 h-3.5 text-[#40CFDE]" />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Leads;
